import { durationOf, type SfxParams } from '../audio/synth';

/**
 * Sound effects v1 (GDD §14.3, issue #76). Each one is a set of synthesiser parameters written for this
 * game and released as CC0, so no file needs a licence beyond this table (docs/CREDITS.md lists them).
 * A downloaded effect would add `file` (under public/audio/sfx) and its own source and licence.
 */
export type SfxId =
  | 'flap'
  | 'dollar'
  | 'impact'
  | 'checkpoint'
  | 'landing'
  // Effects v2 (issue #100).
  | 'intercept'
  | 'decoy-hit'
  | 'jump'
  | 'parachute'
  | 'stamp'
  | 'silver-coin'
  | 'lead-hit'
  | 'en02-amber'
  | 'en02-burst'
  | 'en11-bubbles'
  | 'en11-splash';

export interface SfxEntry {
  id: SfxId;
  /** Only for effects that are files; the synthesised ones have none. */
  file?: string;
  durationMs: number;
  source: string;
  author: string;
  license: 'CC0' | 'jsfxr';
  params: SfxParams;
}

const SOURCE = 'Synthesised in code (src/audio/synth.ts)';
const AUTHOR = 'Narcoverse project';

const entry = (id: SfxId, params: SfxParams): SfxEntry => ({
  id,
  durationMs: Math.round(durationOf(params) * 1000),
  source: SOURCE,
  author: AUTHOR,
  license: 'CC0',
  params,
});

export const SFX_MANIFEST: readonly SfxEntry[] = [
  // Flap: a burst of the engine.
  entry('flap', {
    voices: [
      { wave: 'saw', frequency: 95, slide: 0.9, attack: 0.01, sustain: 0.04, decay: 0.12, volume: 0.35 },
      { wave: 'noise', frequency: 3000, attack: 0.005, sustain: 0.02, decay: 0.09, volume: 0.12 },
    ],
  }),
  // Bill: the ding of a cash register.
  entry('dollar', {
    voices: [
      { wave: 'square', frequency: 988, duty: 0.25, attack: 0.002, sustain: 0.03, decay: 0.08, volume: 0.25 },
      { wave: 'square', frequency: 1319, duty: 0.25, attack: 0.002, sustain: 0.05, decay: 0.2, volume: 0.25, delay: 0.07 },
    ],
  }),
  // Hit: sheet metal and a cough of the engine.
  entry('impact', {
    voices: [
      { wave: 'noise', frequency: 1800, slide: -1.2, attack: 0.002, sustain: 0.06, decay: 0.22, volume: 0.5 },
      { wave: 'saw', frequency: 120, slide: -1.5, attack: 0.005, sustain: 0.08, decay: 0.3, volume: 0.35, delay: 0.05 },
    ],
  }),
  // Checkpoint: the flare and the radio, three rising blips.
  entry('checkpoint', {
    voices: [
      { wave: 'sine', frequency: 660, attack: 0.005, sustain: 0.06, decay: 0.05, volume: 0.3 },
      { wave: 'sine', frequency: 880, attack: 0.005, sustain: 0.06, decay: 0.05, volume: 0.3, delay: 0.13 },
      { wave: 'sine', frequency: 1175, attack: 0.005, sustain: 0.1, decay: 0.12, volume: 0.3, delay: 0.26 },
      { wave: 'noise', frequency: 4000, attack: 0.01, sustain: 0.3, decay: 0.1, volume: 0.04 },
    ],
  }),
  // Landing: wheels on the ground (@gdd-pending, not in the list of GDD §14.3 yet).
  entry('landing', {
    voices: [
      { wave: 'sine', frequency: 140, slide: -1.8, attack: 0.003, sustain: 0.05, decay: 0.2, volume: 0.5 },
      { wave: 'noise', frequency: 900, slide: -1, attack: 0.002, sustain: 0.03, decay: 0.12, volume: 0.25 },
    ],
  }),
  // Effects v2 (issue #100). The radar ping of INTERCEPT is a lone high square blip pair no other warning uses.
  entry('intercept', {
    voices: [
      { wave: 'sine', frequency: 1568, attack: 0.002, sustain: 0.05, decay: 0.12, volume: 0.35 },
      { wave: 'sine', frequency: 1568, attack: 0.002, sustain: 0.05, decay: 0.12, volume: 0.35, delay: 0.22 },
    ],
  }),
  // Decoy hit: a bag of flour bursting (a soft thump and a puff of noise), not verbal (open question §17-4).
  entry('decoy-hit', {
    voices: [
      { wave: 'noise', frequency: 700, slide: -0.8, attack: 0.004, sustain: 0.05, decay: 0.28, volume: 0.4 },
      { wave: 'sine', frequency: 180, slide: -1, attack: 0.003, sustain: 0.04, decay: 0.15, volume: 0.4 },
    ],
  }),
  // Jump: the cockpit door and a whoosh.
  entry('jump', {
    voices: [
      { wave: 'noise', frequency: 2200, slide: -1.4, attack: 0.01, sustain: 0.05, decay: 0.3, volume: 0.28 },
      { wave: 'saw', frequency: 220, slide: -0.6, attack: 0.005, sustain: 0.03, decay: 0.2, volume: 0.15 },
    ],
  }),
  // Parachute: the canopy filling with air.
  entry('parachute', {
    voices: [
      { wave: 'noise', frequency: 500, slide: 1.2, attack: 0.03, sustain: 0.08, decay: 0.2, volume: 0.3 },
      { wave: 'sine', frequency: 120, slide: 0.8, attack: 0.02, sustain: 0.06, decay: 0.16, volume: 0.3 },
    ],
  }),
  // Stamp: the stamp of ENTREGADO, a thud and a short ring.
  entry('stamp', {
    voices: [
      { wave: 'square', frequency: 90, duty: 0.5, slide: -0.7, attack: 0.002, sustain: 0.04, decay: 0.12, volume: 0.45 },
      { wave: 'sine', frequency: 1047, attack: 0.002, sustain: 0.03, decay: 0.25, volume: 0.2, delay: 0.06 },
    ],
  }),
  // Silver coin: a bright high cash-register chime, the opposite of a lead (GDD §13.6).
  entry('silver-coin', {
    voices: [
      { wave: 'sine', frequency: 1760, attack: 0.001, sustain: 0.04, decay: 0.2, volume: 0.3 },
      { wave: 'sine', frequency: 2349, attack: 0.001, sustain: 0.06, decay: 0.28, volume: 0.25, delay: 0.05 },
    ],
  }),
  // Lead: a dull low thud, never a chime.
  entry('lead-hit', {
    voices: [
      { wave: 'saw', frequency: 70, slide: -1.2, attack: 0.002, sustain: 0.05, decay: 0.2, volume: 0.45 },
      { wave: 'noise', frequency: 400, slide: -1, attack: 0.002, sustain: 0.02, decay: 0.12, volume: 0.3 },
    ],
  }),
  // EN-02: the searchlight turning amber is a rising tone; its burst is three quick low pops.
  entry('en02-amber', {
    voices: [{ wave: 'saw', frequency: 330, slide: 0.9, attack: 0.02, sustain: 0.12, decay: 0.12, volume: 0.22 }],
  }),
  entry('en02-burst', {
    voices: [0, 0.09, 0.18].map((delay) => ({ wave: 'noise' as const, frequency: 1200, slide: -1.5, attack: 0.002, sustain: 0.02, decay: 0.07, volume: 0.35, delay })),
  }),
  // EN-11: bubbles in the lagoon and the splash of the jump back into the water.
  entry('en11-bubbles', {
    voices: [
      { wave: 'sine', frequency: 300, slide: 1.5, attack: 0.005, sustain: 0.02, decay: 0.06, volume: 0.25 },
      { wave: 'sine', frequency: 380, slide: 1.5, attack: 0.005, sustain: 0.02, decay: 0.06, volume: 0.22, delay: 0.1 },
      { wave: 'sine', frequency: 450, slide: 1.5, attack: 0.005, sustain: 0.02, decay: 0.06, volume: 0.2, delay: 0.2 },
    ],
  }),
  entry('en11-splash', {
    voices: [
      { wave: 'noise', frequency: 2500, slide: -1, attack: 0.004, sustain: 0.06, decay: 0.3, volume: 0.4 },
      { wave: 'sine', frequency: 110, slide: -1.2, attack: 0.004, sustain: 0.04, decay: 0.18, volume: 0.3 },
    ],
  }),
];

export const sfxEntry = (id: SfxId): SfxEntry => SFX_MANIFEST.find((candidate) => candidate.id === id)!;
